/*
  La Matcha — product finder quiz (sections/product-quiz.liquid).

    <product-quiz>

  Flow      One question at a time. Picking an answer moves on after 260 ms; "back" returns to the
            previous question with its answer still marked. After the last question the first
            result whose condition holds is shown; a sold-out result passes its turn to the next
            match. Every result is already in the page with live product data.
  URL       The answers given so far live in the query, e.g. ?quiz=new_first.how_latte. Each step
            is a history entry, so reload, back, forward and shared links all work.
  Keyboard  The answers are native radio buttons: Tab enters the group, the arrow keys move
            through it without moving on, Space or Enter picks.
  Focus     Moves to the heading of the new question or of the result; the step is announced
            through the page live region, nothing else is.
  Editor    Selecting a question, answer or result block shows it.

  Conditions (data-condition on results and reason lines)
    key=value & other=value     every pair must hold
    key=one|two                 either value
    (empty)                     always holds

  Children:
    [data-quiz-stage]         question view, with [data-quiz-count] (data-template with [current]),
                              [data-quiz-back] and one [data-quiz-bar] per question
    [data-quiz-step]          a question: data-key, [data-quiz-title], radio inputs [data-quiz-option]
    [data-quiz-result]        a result: data-condition, data-available, [data-quiz-result-title],
                              [data-quiz-reason] lines, [data-quiz-upper] product title,
                              [data-quiz-restart]
  Attributes:
    data-uppercase-exceptions  words that keep a plain I when a product title is capitalised
    data-result-text           announced when the result appears
*/
(() => {
  if (customElements.get('product-quiz')) return;

  const LM = window.LM || {};
  const PARAM = 'quiz';
  const ADVANCE_DELAY = 260;

  /* "how=caps & goal=diff|gift" → [{ key: 'how', values: ['caps'] }, { key: 'goal', values: ['diff', 'gift'] }] */
  const parseCondition = (text) =>
    String(text || '')
      .split('&')
      .map((pair) => {
        const index = pair.indexOf('=');
        if (index < 0) return null;
        const key = pair.slice(0, index).trim().toLowerCase();
        const values = pair
          .slice(index + 1)
          .split('|')
          .map((value) => value.trim().toLowerCase())
          .filter(Boolean);
        return key && values.length ? { key, values } : null;
      })
      .filter(Boolean);

  const holds = (rules, answers) => rules.every((rule) => rule.values.includes(answers[rule.key]));

  class ProductQuiz extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;

      this.stage = this.querySelector('[data-quiz-stage]');
      this.count = this.querySelector('[data-quiz-count]');
      this.back = this.querySelector('[data-quiz-back]');
      this.bars = [...this.querySelectorAll('[data-quiz-bar]')];
      this.questions = [...this.querySelectorAll('[data-quiz-step]')].map((el) => ({
        el,
        key: (el.dataset.key || '').toLowerCase(),
        title: el.querySelector('[data-quiz-title]'),
        inputs: [...el.querySelectorAll('[data-quiz-option]')],
      }));
      this.results = [...this.querySelectorAll('[data-quiz-result]')].map((el) => ({
        el,
        rules: parseCondition(el.dataset.condition),
        available: el.dataset.available !== 'false',
        title: el.querySelector('[data-quiz-result-title]'),
        reasons: [...el.querySelectorAll('[data-quiz-reason]')].map((line) => ({
          el: line,
          rules: parseCondition(line.dataset.condition),
        })),
      }));
      if (!this.stage || !this.questions.length) return;

      this.answers = {};
      this.step = 0;
      this.preview = null;
      this.arrowAt = 0;
      this.exceptions = new Set(
        (this.dataset.uppercaseExceptions || '')
          .split(',')
          .map((word) => word.trim().toLowerCase())
          .filter(Boolean)
      );
      this.querySelectorAll('[data-quiz-upper]').forEach((el) => {
        el.textContent = this.upper(el.textContent);
      });

      this.addEventListener('keydown', (event) => this.onKeydown(event));
      this.addEventListener('click', (event) => this.onClick(event));
      this.onPopState = () => {
        window.clearTimeout(this.timer);
        this.readUrl();
        this.render();
        this.focusCurrent();
      };
      window.addEventListener('popstate', this.onPopState);

      if (LM.designMode) {
        this.addEventListener('shopify:block:select', (event) => this.onBlockSelect(event));
      }

      this.readUrl();
      this.render();
    }

    disconnectedCallback() {
      window.clearTimeout(this.timer);
      window.removeEventListener('popstate', this.onPopState);
    }

    get done() {
      return this.step >= this.questions.length;
    }

    /* Capitals in the page language. Words listed in the theme setting keep a plain I. */
    upper(text) {
      const lang = document.documentElement.lang || undefined;
      return String(text)
        .trim()
        .split(/([\s\-–/]+)/)
        .map((part) => (this.exceptions.has(part.toLowerCase()) ? part.toUpperCase() : part.toLocaleUpperCase(lang)))
        .join('');
    }

    /* ---------- URL ---------- */
    /* Reads the answers from the query. The current step is the first question without a valid answer. */
    readUrl() {
      const given = {};
      (new URL(window.location.href).searchParams.get(PARAM) || '').split('.').forEach((pair) => {
        const index = pair.indexOf('_');
        if (index > 0) given[pair.slice(0, index)] = pair.slice(index + 1);
      });
      let step = 0;
      while (step < this.questions.length) {
        const question = this.questions[step];
        const value = given[question.key];
        if (!value || !question.inputs.some((input) => input.value === value)) break;
        this.answers[question.key] = value;
        step += 1;
      }
      this.step = step;
      this.preview = null;
    }

    writeUrl() {
      const url = new URL(window.location.href);
      const value = this.questions
        .slice(0, this.step)
        .map((question) => `${question.key}_${this.answers[question.key]}`)
        .join('.');
      if (value) url.searchParams.set(PARAM, value);
      else url.searchParams.delete(PARAM);
      if (url.href !== window.location.href) window.history.pushState({ quiz: this.step }, '', url);
    }

    /* ---------- Events ---------- */
    onKeydown(event) {
      const input = event.target instanceof Element ? event.target.closest('[data-quiz-option]') : null;
      if (!input) return;
      if (event.key.startsWith('Arrow')) {
        // The browser moves the selection and fires a click; that click must not move on.
        this.arrowAt = performance.now();
      } else if (event.key === 'Enter' || event.key === ' ') {
        // Browsers ignore Space on a radio button that is already checked, and Enter altogether.
        event.preventDefault();
        input.checked = true;
        this.pick(input);
      }
    }

    onClick(event) {
      if (!(event.target instanceof Element)) return;
      if (event.target.closest('[data-quiz-back]')) {
        this.go(this.step - 1);
        return;
      }
      if (event.target.closest('[data-quiz-restart]')) {
        this.answers = {};
        this.go(0);
        return;
      }
      // A click on the label reaches the radio button as a second click event.
      if (!event.target.matches('[data-quiz-option]')) return;
      if (performance.now() - this.arrowAt < 250) return;
      this.pick(event.target);
    }

    onBlockSelect(event) {
      if (!(event.target instanceof Element)) return;
      window.clearTimeout(this.timer);
      const result = event.target.closest('[data-quiz-result]');
      if (result) {
        this.preview = this.results.find((item) => item.el === result) || null;
        this.step = this.questions.length;
        this.render();
        return;
      }
      const step = event.target.closest('[data-quiz-step]');
      const index = this.questions.findIndex((question) => question.el === step);
      if (index < 0) return;
      this.preview = null;
      this.step = index;
      this.render();
    }

    /* ---------- Flow ---------- */
    pick(input) {
      const question = this.questions.find((item) => item.inputs.includes(input));
      if (!question || question !== this.questions[this.step]) return;
      this.answers[question.key] = input.value;
      window.clearTimeout(this.timer);
      this.timer = window.setTimeout(() => this.go(this.step + 1), ADVANCE_DELAY);
    }

    go(step) {
      window.clearTimeout(this.timer);
      this.step = Math.max(0, Math.min(this.questions.length, step));
      this.preview = null;
      this.writeUrl();
      this.render();
      this.focusCurrent();
      this.scrollIntoPlace();
      this.announce();
    }

    /* The first result whose condition holds; one that is sold out passes its turn. */
    decide() {
      const matches = this.results.filter((result) => holds(result.rules, this.answers));
      return matches.find((result) => result.available) || matches[0] || this.results[0] || null;
    }

    render() {
      const done = this.done;
      this.stage.hidden = done;

      this.questions.forEach((question, index) => {
        question.el.hidden = done || index !== this.step;
        question.inputs.forEach((input) => {
          input.checked = this.answers[question.key] === input.value;
        });
      });
      this.bars.forEach((bar, index) => {
        bar.classList.toggle('is-done', index < this.step);
        bar.classList.toggle('is-current', index === this.step);
      });
      if (this.count && !done) {
        this.count.textContent = (this.count.dataset.template || '').replace('[current]', String(this.step + 1));
      }
      if (this.back) this.back.hidden = this.step === 0;

      const shown = done ? this.preview || this.decide() : null;
      this.results.forEach((result) => {
        result.el.hidden = result !== shown;
      });
      if (shown) {
        // In the editor preview every reason line shows; otherwise only the ones for these answers.
        shown.reasons.forEach((reason) => {
          reason.el.hidden = !this.preview && !holds(reason.rules, this.answers);
        });
      }
      this.shown = shown;
    }

    focusCurrent() {
      const target = this.done ? this.shown && this.shown.title : this.questions[this.step].title;
      if (target) target.focus({ preventScroll: true });
    }

    /* Small screens, and any screen where the quiz has scrolled away, get the new step in view. */
    scrollIntoPlace() {
      const rect = this.getBoundingClientRect();
      const small = window.matchMedia('(max-width: 749px)').matches;
      if (!small && rect.top >= 0 && rect.top < window.innerHeight * 0.5) return;
      this.scrollIntoView({ behavior: LM.reduceMotion && LM.reduceMotion() ? 'auto' : 'smooth', block: 'start' });
    }

    announce() {
      if (!LM.announce) return;
      const message = this.done ? this.dataset.resultText : this.count && this.count.textContent.trim();
      if (message) LM.announce(message);
    }
  }

  customElements.define('product-quiz', ProductQuiz);
})();
